// Thin, framework-agnostic wrapper around RTCPeerConnection.
//
// Responsibilities:
//   - create a peer connection with the supplied ICE servers,
//   - attach a local MediaStream (audio +/- video),
//   - surface onIceCandidate / onTrack / onStateChange events,
//   - offer/answer helpers,
//   - teardown that releases every track and transceiver.
//
// The higher-level store (`callStore.ts`) wires this into socket signaling.

export type PeerConnectionState = RTCPeerConnectionState;

export interface CallPeerOptions {
  iceServers: RTCIceServer[];
  onIceCandidate: (candidate: RTCIceCandidateInit) => void;
  onTrack: (stream: MediaStream) => void;
  onStateChange: (state: PeerConnectionState) => void;
}

export class CallPeer {
  private pc: RTCPeerConnection;
  private remoteStream: MediaStream = new MediaStream();
  private localStream: MediaStream | null = null;
  // Queue for ICE candidates that arrive before setRemoteDescription.
  private pendingRemoteCandidates: RTCIceCandidateInit[] = [];
  private remoteDescSet = false;
  private closed = false;

  constructor(opts: CallPeerOptions) {
    this.pc = new RTCPeerConnection({
      iceServers: opts.iceServers,
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
    });

    this.pc.addEventListener('icecandidate', (ev) => {
      if (ev.candidate) {
        opts.onIceCandidate(ev.candidate.toJSON());
      }
    });

    this.pc.addEventListener('track', (ev) => {
      for (const track of ev.streams[0]?.getTracks() ?? [ev.track]) {
        if (!this.remoteStream.getTracks().find((t) => t.id === track.id)) {
          this.remoteStream.addTrack(track);
        }
      }
      opts.onTrack(this.remoteStream);
    });

    this.pc.addEventListener('connectionstatechange', () => {
      opts.onStateChange(this.pc.connectionState);
    });
  }

  attachLocalStream(stream: MediaStream): void {
    this.localStream = stream;
    for (const track of stream.getTracks()) {
      this.pc.addTrack(track, stream);
    }
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    const offer = await this.pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    });
    await this.pc.setLocalDescription(offer);
    return offer;
  }

  async acceptOfferAndAnswer(
    offer: RTCSessionDescriptionInit,
  ): Promise<RTCSessionDescriptionInit> {
    await this.pc.setRemoteDescription(offer);
    this.remoteDescSet = true;
    await this.drainPendingCandidates();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return answer;
  }

  async acceptAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(answer);
    this.remoteDescSet = true;
    await this.drainPendingCandidates();
  }

  async addRemoteCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.remoteDescSet) {
      this.pendingRemoteCandidates.push(candidate);
      return;
    }
    try {
      await this.pc.addIceCandidate(candidate);
    } catch {
      // Chrome sometimes throws on duplicate / null candidates — swallow.
    }
  }

  private async drainPendingCandidates() {
    const queue = this.pendingRemoteCandidates.slice();
    this.pendingRemoteCandidates = [];
    for (const c of queue) {
      try {
        await this.pc.addIceCandidate(c);
      } catch {
        /* ignore */
      }
    }
  }

  setMicEnabled(enabled: boolean): void {
    if (!this.localStream) return;
    for (const t of this.localStream.getAudioTracks()) t.enabled = enabled;
  }

  setCameraEnabled(enabled: boolean): void {
    if (!this.localStream) return;
    for (const t of this.localStream.getVideoTracks()) t.enabled = enabled;
  }

  getRemoteStream(): MediaStream {
    return this.remoteStream;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;

    // Stop every local track first so the browser releases mic/camera.
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        try { track.stop(); } catch { /* ignore */ }
      }
      this.localStream = null;
    }

    // Drop senders / transceivers explicitly — safer across Chromium versions.
    try {
      for (const sender of this.pc.getSenders()) {
        if (sender.track) sender.track.stop();
      }
    } catch { /* ignore */ }

    try {
      this.pc.close();
    } catch { /* ignore */ }
  }
}

// ── Local media helpers ─────────────────────────────────────────────────────

export class MediaPermissionError extends Error {
  constructor(public readonly kind: 'denied' | 'no-device' | 'unsupported' | 'other') {
    super(kind);
    this.name = 'MediaPermissionError';
  }
}

export async function getLocalStream(mode: 'VOICE' | 'VIDEO'): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new MediaPermissionError('unsupported');
  }

  const constraints: MediaStreamConstraints = {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video:
      mode === 'VIDEO'
        ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }
        : false,
  };

  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (err) {
    const name = (err as { name?: string })?.name ?? '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      throw new MediaPermissionError('denied');
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      throw new MediaPermissionError('no-device');
    }
    throw new MediaPermissionError('other');
  }
}
